import React from 'react';

import classNames from 'classnames';
import NumberFormat from 'react-number-format';

import { formatFigure, formatMoneyWithLocale } from 'Helpers/formatService';

import EditIcon from 'Assets/icons/edit.svg';

const moneyFigure = (value) => formatFigure(value, formatMoneyWithLocale, { precision: 3 });

/**
 * TicketingCommitmentLinesTable
 *
 * The single pre-loaded commitment line of the Commit Ticketing screen — the ticketing
 * sibling of CommitmentLinesTable (Direct Commitment), sharing its blue header.
 *
 * A ticketing commitment is one total amount against the event's ticketing revenue, so there
 * is exactly one row and one editable figure: the committing amount, pre-loaded with the full
 * uncommitted balance and edited in place.
 *
 * Props:
 *  - budgeted:      the event's budgeted ticketing value
 *  - amount:        the committing amount (a raw input string, so a half-typed value survives)
 *  - amountInvalid: the amount is <= 0 or over the uncommitted balance
 *  - onAmountChange
 */
export default function TicketingCommitmentLinesTable({ budgeted = 0, amount = '', amountInvalid = false, onAmountChange }) {
    // Written out in full rather than interpolated: Tailwind only emits classes it can see
    // literally in the source, so a computed `tw-text-${align}` would not be generated.
    const headerCell = (label, alignEnd = false) => (
        <th className={classNames('tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3', alignEnd ? 'tw-text-end' : 'tw-text-start')}>
            {label}
        </th>
    );

    return (
        <div className="tw-rounded-xl tw-border tw-border-charcoal-15 tw-overflow-hidden tw-shadow-sm">
            <table className="tw-w-full inbud-table tw-text-sm">
                <thead>
                    <tr className="tw-bg-blue-25">
                        {headerCell($t('Budget line'))}
                        {headerCell($t('Budgeted'), true)}
                        {headerCell($t('Committing amount'), true)}
                    </tr>
                </thead>
                <tbody>
                    <tr className="tw-border-b tw-border-charcoal-15">
                        <td className="tw-p-3 tw-text-charcoal tw-font-semibold">{$t('Ticketing')}</td>
                        <td className="tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal tw-font-medium">{moneyFigure(budgeted)}</td>
                        <td className="tw-p-3 tw-text-end">
                            <div
                                className={classNames(
                                    'tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-px-2 tw-py-1',
                                    amountInvalid ? 'tw-border-red tw-bg-red-10' : 'tw-border-blue tw-bg-blue-10',
                                )}
                            >
                                {/* NumberFormat, not a bare number input: a number input cannot carry
                                    thousands separators, which left the one figure being typed as the
                                    only unseparated number on the screen. onValueChange hands the
                                    parent the UNformatted digits, so its arithmetic is unaffected.
                                    text-sm AND font-medium match the read-only cells beside it, per
                                    Dave's instruction that the committing inputs match the
                                    typography used elsewhere on the screen. The box, tint and
                                    pencil are the wireframe's, and stay. */}
                                <NumberFormat
                                    value={amount}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    allowNegative={false}
                                    inputMode="decimal"
                                    aria-label={$t('Committing amount')}
                                    onValueChange={({ value }) => onAmountChange && onAmountChange(value)}
                                    className="tw-w-32 tw-border-0 tw-bg-transparent tw-p-0 tw-text-end tw-text-sm tw-font-medium tw-tabular-nums tw-text-charcoal focus:tw-outline-none"
                                />
                                <EditIcon className="tw-w-3.5 tw-h-3.5 tw-shrink-0 tw-text-blue" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
